// Create and edit a quotation.
//
// Parallel to InvoiceEditor, sharing the line table, totals panel and pickers.
// Differs in the header fields (validity instead of payment method), the status
// set, and the ability to turn the quotation into an invoice.

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { fieldErrors, useAsync, useErrorMessage } from '../lib/hooks';
import { useI18n, useT } from '../lib/i18n';
import { useSession } from '../lib/session';
import { useToast } from '../components/Toast';
import { Icon } from '../components/Icon';
import { ClientCombobox } from '../components/ClientCombobox';
import { ClientForm } from '../components/ClientForm';
import { ProductForm } from '../components/ProductForm';
import { ProductPicker, type PickedLine } from '../components/ProductPicker';
import { EmailDialog } from '../components/EmailDialog';
import {
  addLineTo, DocumentLines, newLineKey, TotalsPanel, type DraftLine,
} from '../components/DocumentLines';
import { useDocumentTotals } from '../components/DocumentLines';
import {
  ConfirmDialog, ErrorAlert, LoadingRow, Modal, SelectField, TextAreaField, TextField,
} from '../components/ui';
import { toDateInput } from '@shared/format';
import {
  QUOTATION_STATUSES, type Client, type QuotationInput, type QuotationStatus, type User,
} from '@shared/types';

export function QuotationEditor() {
  const params = useParams<{ id: string }>();
  const isNew = params.id === 'new';
  const quotationId = isNew ? undefined : Number(params.id);

  const t = useT();
  const { locale } = useI18n();
  const { taxRate: settingsTaxRate, money } = useSession();
  const toast = useToast();
  const navigate = useNavigate();
  const toMessage = useErrorMessage();

  const [client, setClient] = useState<Client>();
  const [sellerId, setSellerId] = useState<number | ''>('');
  const [issuedAt, setIssuedAt] = useState(toDateInput(new Date()));
  const [validUntil, setValidUntil] = useState('');
  const [status, setStatus] = useState<QuotationStatus>('draft');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [taxRate, setTaxRate] = useState(settingsTaxRate);
  const [quotationNumber, setQuotationNumber] = useState<number>();
  const [currencySymbol, setCurrencySymbol] = useState(money.symbol);
  const [convertedTo, setConvertedTo] = useState<{ id: number; number: number }>();

  const [modal, setModal] = useState<'products' | 'client' | 'product' | 'email' | undefined>();
  const [confirmConvert, setConfirmConvert] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string>();
  const [saving, setSaving] = useState(false);
  const [converting, setConverting] = useState(false);
  const [dirty, setDirty] = useState(false);

  const sellers = useAsync(() => api.activeUsers(), []);

  const load = useCallback(async () => {
    if (quotationId === undefined) {
      const defaults = await api.quotationDefaults();
      setIssuedAt(defaults.issuedAt);
      setValidUntil(defaults.validUntil);
      setTaxRate(defaults.taxRate);
      setCurrencySymbol(defaults.currency.symbol);
      if (defaults.sellerId !== null) setSellerId(defaults.sellerId);
      return;
    }
    const quotation = await api.quotation(quotationId);
    setClient({
      id: quotation.clientId, name: quotation.clientName, taxId: quotation.clientTaxId,
      phone: quotation.clientPhone, email: quotation.clientEmail, address: quotation.clientAddress,
      isActive: true, createdAt: quotation.issuedAt,
    });
    setSellerId(quotation.sellerId);
    setIssuedAt(toDateInput(quotation.issuedAt));
    setValidUntil(toDateInput(quotation.validUntil));
    setStatus(quotation.status);
    setNotes(quotation.notes);
    setTaxRate(quotation.taxRate);
    setQuotationNumber(quotation.number);
    setCurrencySymbol(quotation.currencySymbol);
    setConvertedTo(quotation.convertedInvoiceId !== null && quotation.convertedInvoiceNumber !== null
      ? { id: quotation.convertedInvoiceId, number: quotation.convertedInvoiceNumber }
      : undefined);
    setLines(quotation.lines.map((line) => ({
      key: newLineKey(),
      productId: line.productId,
      code: line.code,
      description: line.description,
      quantity: line.quantity,
      unitPrice: line.unitPrice,
    })));
    setDirty(false);
  }, [quotationId]);

  const { loading, error, reload } = useAsync(load, [quotationId]);

  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => { event.preventDefault(); };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  // Once invoiced, the quotation is the record of what was agreed.
  const locked = convertedTo !== undefined;
  const touch = () => setDirty(true);

  const addLine = (line: PickedLine) => {
    setLines((prev) => addLineTo(prev, line));
    touch();
    toast.success(t('invoice.picker.added', { name: line.description }));
  };
  const updateLine = (key: string, patch: Partial<DraftLine>) => {
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));
    touch();
  };
  const removeLine = (key: string) => {
    setLines((prev) => prev.filter((l) => l.key !== key));
    touch();
  };

  const totals = useDocumentTotals(lines, taxRate);

  async function save() {
    setErrors({});
    setFormError(undefined);

    const localErrors: Record<string, string> = {};
    if (!client) localErrors.clientId = t('error.invoiceClientRequired');
    if (sellerId === '') localErrors.sellerId = t('error.required');
    if (!validUntil) localErrors.validUntil = t('error.required');
    if (lines.length === 0) localErrors.lines = t('error.invoiceNoLines');
    if (Object.keys(localErrors).length > 0) {
      setErrors(localErrors);
      if (localErrors.lines) setFormError(localErrors.lines);
      return;
    }

    const payload: QuotationInput = {
      clientId: client!.id,
      sellerId: Number(sellerId),
      issuedAt,
      validUntil,
      status,
      notes,
      lines: lines.map((l) => ({
        productId: l.productId, code: l.code, description: l.description,
        quantity: l.quantity, unitPrice: l.unitPrice,
      })),
    };

    setSaving(true);
    try {
      const saved = quotationId === undefined
        ? await api.createQuotation(payload)
        : await api.updateQuotation(quotationId, payload);
      setDirty(false);
      toast.success(t(quotationId === undefined ? 'quotation.created' : 'quotation.updated',
        { number: saved.number }));
      navigate(`/quotations/${saved.id}`, { replace: true });
      if (quotationId !== undefined) reload();
    } catch (err) {
      setErrors(fieldErrors(err));
      setFormError(toMessage(err));
    } finally {
      setSaving(false);
    }
  }

  async function convert() {
    if (quotationId === undefined) return;
    setConverting(true);
    try {
      const result = await api.convertQuotation(quotationId, 'transfer');
      toast.success(t('quotation.converted', { number: result.invoiceNumber }));
      navigate(`/invoices/${result.invoiceId}`);
    } catch (err) {
      toast.error(toMessage(err));
      setConverting(false);
      setConfirmConvert(false);
    }
  }

  if (loading) return <LoadingRow />;
  if (error) return <ErrorAlert message={error} onRetry={reload} />;

  const sellerOptions: User[] = sellers.data ?? [];

  return (
    <>
      <div className="page-head">
        <div>
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => navigate('/quotations')}>
            <Icon name="arrow-left" size={14} />{t('quotations.title')}
          </button>
          <h1 className="page-head__title" style={{ marginTop: 6 }}>
            {quotationNumber !== undefined
              ? t('quotation.edit.title', { number: quotationNumber })
              : t('quotation.new.title')}
          </h1>
        </div>
        <div className="page-head__actions">
          {quotationId !== undefined && (
            <>
              <button type="button" className="btn" onClick={() => setModal('email')}>
                <Icon name="mail" />{t('email.send')}
              </button>
              <a className="btn" href={api.quotationPdfUrl(quotationId, locale)}>
                <Icon name="download" />{t('quotations.download')}
              </a>
              <button type="button" className="btn" disabled={locked || converting}
                onClick={() => setConfirmConvert(true)}>
                <Icon name="arrow-right-circle" />{t('quotation.convert')}
              </button>
            </>
          )}
          {!locked && (
            <button type="button" className="btn btn--primary" onClick={() => void save()} disabled={saving}>
              {saving ? <span className="spinner" /> : <Icon name="save" />}
              {saving ? t('common.saving') : t('quotation.save')}
            </button>
          )}
        </div>
      </div>

      {convertedTo && (
        <div className="alert alert--info" style={{ marginBottom: 14 }}>
          <Icon name="check-circle" />
          <div style={{ flex: 1 }}>{t('quotation.convertedBanner', { number: convertedTo.number })}</div>
          <button type="button" className="btn btn--sm" onClick={() => navigate(`/invoices/${convertedTo.id}`)}>
            {t('quotation.convertedOpen')}
          </button>
        </div>
      )}
      {formError && <div style={{ marginBottom: 14 }}><ErrorAlert message={formError} /></div>}

      <div className="editor-grid">
        <div className="stack">
          <div className="card">
            <div className="card__body">
              <div className="form-grid form-grid--2">
                <div className="field span-all">
                  <label className="field__label">{t('invoice.field.client')}</label>
                  <ClientCombobox value={client} error={errors.clientId} disabled={locked}
                    onSelect={(next) => { setClient(next); touch(); }} />
                  {errors.clientId
                    ? <div className="field__error">{errors.clientId}</div>
                    : client && (
                      <div className="field__hint">
                        {[client.taxId, client.phone, client.email].filter(Boolean).join(' · ') || ' '}
                      </div>
                    )}
                </div>

                <SelectField label={t('invoice.field.seller')} value={sellerId} disabled={locked}
                  error={errors.sellerId}
                  onChange={(e) => { setSellerId(Number(e.target.value)); touch(); }}>
                  <option value="" disabled>—</option>
                  {sellerOptions.map((user) => (
                    <option key={user.id} value={user.id}>{user.firstName} {user.lastName}</option>
                  ))}
                </SelectField>

                <SelectField label={t('invoice.field.status')} value={status} disabled={locked}
                  onChange={(e) => { setStatus(e.target.value as QuotationStatus); touch(); }}>
                  {QUOTATION_STATUSES.map((s) => (
                    <option key={s} value={s}>{t(`status.${s}` as const)}</option>
                  ))}
                </SelectField>

                <TextField label={t('invoice.field.date')} type="date" value={issuedAt}
                  disabled={locked} error={errors.issuedAt}
                  onChange={(e) => { setIssuedAt(e.target.value); touch(); }} />

                <TextField label={t('quotation.field.validUntil')} type="date" value={validUntil}
                  disabled={locked} error={errors.validUntil} hint={t('quotation.validityHint')}
                  onChange={(e) => { setValidUntil(e.target.value); touch(); }} />
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card__head">
              <h2 className="card__title">{t('invoice.addLines')}</h2>
              {!locked && (
                <div className="btn-group">
                  <button type="button" className="btn btn--sm" onClick={() => setModal('client')}>
                    <Icon name="users" size={14} />{t('invoice.newClient')}
                  </button>
                  <button type="button" className="btn btn--sm" onClick={() => setModal('product')}>
                    <Icon name="box" size={14} />{t('invoice.newProduct')}
                  </button>
                  <button type="button" className="btn btn--primary btn--sm" onClick={() => setModal('products')}>
                    <Icon name="plus" size={14} />{t('invoice.addLines')}
                  </button>
                </div>
              )}
            </div>

            <DocumentLines
              lines={lines}
              currencySymbol={currencySymbol}
              readOnly={locked}
              onChange={updateLine}
              onRemove={removeLine}
              emptyAction={!locked && (
                <button type="button" className="btn btn--primary" onClick={() => setModal('products')}>
                  <Icon name="plus" />{t('invoice.addLines')}
                </button>
              )}
            />
          </div>
        </div>

        <aside className="editor-side">
          <TotalsPanel subtotal={totals.subtotal} tax={totals.tax} total={totals.total}
            taxRate={taxRate} currencySymbol={currencySymbol} note={t('invoice.taxRateNote')} />
          <div className="card">
            <div className="card__body">
              <TextAreaField label={t('invoice.field.notes')} optional rows={4} disabled={locked}
                placeholder={t('invoice.field.notesPlaceholder')}
                value={notes} onChange={(e) => { setNotes(e.target.value); touch(); }} />
            </div>
          </div>
        </aside>
      </div>

      {modal === 'products' && (
        <Modal title={t('invoice.picker.title')} wide onClose={() => setModal(undefined)}
          footer={<button type="button" className="btn btn--primary" onClick={() => setModal(undefined)}>
            {t('common.close')}</button>}>
          <ProductPicker onAdd={addLine} />
        </Modal>
      )}

      {modal === 'client' && (
        <Modal title={t('clients.new')} onClose={() => setModal(undefined)}>
          <ClientForm onCancel={() => setModal(undefined)} onSaved={(saved) => {
            setClient(saved); setModal(undefined); touch(); toast.success(t('clients.created'));
          }} />
        </Modal>
      )}

      {modal === 'product' && (
        <Modal title={t('products.new')} onClose={() => setModal(undefined)}>
          <ProductForm onCancel={() => setModal(undefined)} onSaved={(saved) => {
            addLine({ productId: saved.id, code: saved.code, description: saved.name,
              quantity: 1, unitPrice: saved.unitPrice });
            setModal(undefined);
          }} />
        </Modal>
      )}

      {modal === 'email' && quotationId !== undefined && (
        <EmailDialog kind="quotation" id={quotationId}
          documentLabel={`${t('quotations.title')} ${quotationNumber} · ${client?.name ?? ''}`}
          onClose={() => setModal(undefined)} onSent={reload} />
      )}

      {confirmConvert && quotationNumber !== undefined && (
        <ConfirmDialog
          title={t('quotation.convert')}
          message={t('quotation.convertConfirm', { number: quotationNumber })}
          confirmLabel={t('quotation.convert')}
          busy={converting}
          onConfirm={() => void convert()}
          onCancel={() => setConfirmConvert(false)}
        />
      )}
    </>
  );
}
